import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Button, Grid, Header, Icon, Image, Modal } from 'semantic-ui-react';
import { Field } from 'formik';
import { EstateFormProps } from '../UpdateEstateForm';
import { list } from '../../../services/insurer';
import { dangerNotification } from '../../../services/notification';
import Input from '../../../components/Input';
import Toggle from '../../../components/Toggle';
import SectionLabel from '../../../components/SectionLabel';
import { FormikErrorMessage } from '../../../components/ErrorMessage';
import { FieldLabel } from '../../../styles';
import {
  FireInsurersRow,
  InsurerCard,
  InsurerCardContent,
  InsurerCardContentTitle,
  InsurerCardIcon,
  InsurerCardToggleContent,
  NoParamsMessage
} from '../styles';
import { InsurersCodeEnum } from '../../../enums/insurers-code.enum';
import styled from 'styled-components';
import AlfaFireInsuranceConfig from '../../estates-form/components/AlfaFireInsuranceConfig';
import YelumFireInsuranceConfig from '../../estates-form/components/YelumFireInsuranceConfig';
import IconButton from '../../../components/IconButton';
import { DropdownOptions } from '../../find-record/types/options-find.type';
import { get } from '../../../services/request';

export const FireInsuranceData = (props: EstateFormProps) => {
  const { errors, touched, handleInputChange, values, setFieldValue } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [availableInsurers, setAvailableInsurers] = useState<any[]>([]);
  const [isLibertySelected, setIsLibertySelected] = useState<boolean>(false);
  const [isPottencialSelected, setIsPottencialSelected] = useState<boolean>(
    values.isPottencialFireInsuranceSelected
  );
  const [isTokioMarineSegimobSelected, setIsTokioMarineSegimobSelected] =
    useState<boolean>(values.isTokioMarineSegimobSelected);
  const [insurerSelected, setInsurerSelected] = useState<any>();

  const sanitizePercentageInput = (value: string) => {
    const normalizedValue = String(value ?? '')
      .replace(/,/g, '.')
      .replace(/[^\d.]/g, '');
    const firstDecimalSeparatorIndex = normalizedValue.indexOf('.');

    if (firstDecimalSeparatorIndex === -1) {
      return normalizedValue;
    }

    const integerPart = normalizedValue.slice(0, firstDecimalSeparatorIndex);
    const decimalPart = normalizedValue
      .slice(firstDecimalSeparatorIndex + 1)
      .replace(/\./g, '')
      .slice(0, 2);

    return `${integerPart}.${decimalPart}`;
  };

  const formatPercentageInput = (value: string) => {
    const sanitizedValue = sanitizePercentageInput(value);

    if (!sanitizedValue || sanitizedValue === '.') {
      return '';
    }

    const numericValue = Number(sanitizedValue);

    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return Math.min(Math.max(numericValue, 0), 100).toFixed(2);
  };

  const handlePercentageChange = (data: any) => {
    setFieldValue(data.field.name, sanitizePercentageInput(data.value));
  };

  const handlePercentageBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(e.target.name, formatPercentageInput(e.target.value));
  };

  useEffect(() => {
    setIsLoading(true);

    list({ availability: 'FIRE_INSURANCE' })
      .then(result => {
        setAvailableInsurers(result);
      })
      .catch(error => {
        dangerNotification(
          'Oops...',
          'Não foi possível buscar as seguradoras disponíveis'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });

    setIsLibertySelected(
      props.values.isLibertyFireInsuranceSelected ||
        values?.fireInsurers?.find((i: any) => i.code === InsurersCodeEnum.Liberty)
    );
  }, []);

  const handleToggle = async (e: SyntheticEvent, value: any) => {
    if (value) {
      const insurer = availableInsurers.find(i => i.id == value.name);
      if (value.checked && insurer) {
        if (value.id.includes(InsurersCodeEnum.Liberty)) {
          setIsLibertySelected(true);
          setFieldValue('isLibertyFireInsuranceSelected', true);
        }

        if (value.id.includes(InsurersCodeEnum.Pottencial)) {
          setFieldValue('isPottencialFireInsuranceSelected', true);
          setIsPottencialSelected(true);
        }

        if (value.id.includes(InsurersCodeEnum.TokioMarineSegimob)) {
          setFieldValue('isTokioMarineSegimobSelected', true);
          setIsTokioMarineSegimobSelected(true);
        }

        values.fireInsurers.push({
          id: insurer.id,
          code: insurer.code,
          name: insurer.name,
          imageUrl: insurer.image
        });
      } else if (!value.checked && insurer) {
        if (value.id.includes(InsurersCodeEnum.Liberty)) {
          setIsLibertySelected(false);
          setFieldValue('isLibertyFireInsuranceSelected', false);
        }

        if (value.id.includes(InsurersCodeEnum.Pottencial)) {
          setFieldValue('isPottencialFireInsuranceSelected', false);
          setIsPottencialSelected(false);
        }

        if (value.id.includes(InsurersCodeEnum.TokioMarineSegimob)) {
          setFieldValue('isTokioMarineSegimobSelected', false);
          setIsTokioMarineSegimobSelected(false);
        }

        values.fireInsurers = values.fireInsurers.filter((i: any) => i.id != value.name);
      }
      setFieldValue(`values.fireInsurers`, values.fireInsurers);
    }
  };

  const [openModalConfig, setOpenModalConfig] = useState(false);
  const [plansOptions, setPlansOptions] = useState<DropdownOptions[]>([]);
  const [allPlans, setAllPlans] = useState<any[]>([]);

  interface PlanInterface {
    id: string;
    channelId: string;
    name: string;
    code: string;
  }

  useEffect(() => {
    const fetchPlans = async () => {
      const allPlans = await get('/alfa-seguros/plan');

      setAllPlans(allPlans);
      setPlansOptions(
        allPlans.map((plan: PlanInterface) => ({
          key: plan.id,
          text: plan.name,
          value: plan.id
        }))
      );
    };

    fetchPlans();
  }, []);

  const FireInsuranceConfigPotencialContentModal = () => {
    return (
      <Grid>
        <Grid.Row columns={'equal'}>
          <Grid.Column>
            <FieldLabel>Comissão Padrão (Porcentagem)* </FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.fireInsurance.commissionPercentage"
              onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) =>
                handlePercentageChange(data)
              }
              onBlur={handlePercentageBlur}
              type="text"
              inputMode="decimal"
              component={Input}
              className={
                errors &&
                errors.pottencialfireInsuranceCommissionPercentage &&
                touched &&
                touched.pottencialfireInsuranceCommissionPercentage
                  ? 'text-input error'
                  : 'text-input'
              }
              fluid
            />
            <FormikErrorMessage
              component="div"
              name="insurerSpecificData.pottencial.fireInsurance.commissionPercentage"
            />
          </Grid.Column>
          <Grid.Column>
            <FieldLabel>Multiplicador*</FieldLabel>
            <Field
              name="insurerSpecificData.pottencial.fireInsurance.multiplier"
              component={Input}
              className={
                errors &&
                errors.pottencialfireInsuranceCommissionMultiplier &&
                touched &&
                touched.pottencialfireInsuranceCommissionMultiplier
                  ? 'text-input error'
                  : 'text-input'
              }
              onChange={(e: SyntheticEvent, data: any) => handleInputChange(data, props)}
              fluid
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value.replace(/\D/g, ''));
                if (Number(value) > 500) {
                  setFieldValue(
                    'insurerSpecificData.pottencial.fireInsurance.multiplier',
                    500
                  );
                  return;
                }

                if (Number(value) < 100) {
                  setFieldValue(
                    'insurerSpecificData.pottencial.fireInsurance.multiplier',
                    100
                  );
                  return;
                }
                setFieldValue(
                  'insurerSpecificData.pottencial.fireInsurance.multiplier',
                  value
                );
              }}
              type="number"
              max={500}
              min={100}
            />
            <FormikErrorMessage
              component="div"
              name="insurerSpecificData.pottencial.fireInsurance.multiplier"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const FireInsuranceConfigTokioMarineSegimobContentModal = () => {
    return (
      <Grid>
        <Grid.Row columns={'equal'}>
          <Grid.Column>
            <FieldLabel>Código Segimob </FieldLabel>
            <Field
              name="segimobCode"
              onChange={(e: React.ChangeEvent<HTMLInputElement>, data: any) => {
                handleInputChange(data, props);
              }}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('segimobCode', e.target.value);
              }}
              component={Input}
              fluid
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const FireInsuranceConfigModal = (props: any) => {
    const { name } =
      availableInsurers.find((i: any) => i.id === insurerSelected.id) ?? {};

    return (
      <div>
        <Modal
          style={{ height: 'auto', width: '50vw', zIndex: 10 }}
          closeIcon
          open={openModalConfig}
          onClose={() => setOpenModalConfig(false)}
        >
          <Header style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex' }}>
              <Icon name={'file alternate outline'} /> {name && `${name}`}
            </div>
          </Header>
          <Modal.Content scrolling>
            {insurerSelected?.code.includes(InsurersCodeEnum.Pottencial) &&
              FireInsuranceConfigPotencialContentModal()}
            {insurerSelected?.code.includes(InsurersCodeEnum.alfa) && (
              <AlfaFireInsuranceConfig
                {...props}
                plansOptions={plansOptions}
                allPlans={allPlans}
              />
            )}
            {insurerSelected.code === InsurersCodeEnum.TokioMarineSegimob &&
              FireInsuranceConfigTokioMarineSegimobContentModal()}
            {insurerSelected.code === InsurersCodeEnum.Yelum && (
              <YelumFireInsuranceConfig {...props} />
            )}
            {insurerSelected &&
              ![
                InsurersCodeEnum.Pottencial,
                InsurersCodeEnum.alfa,
                InsurersCodeEnum.TokioMarineSegimob,
                InsurersCodeEnum.Yelum
              ].includes(insurerSelected?.code) && (
                <NoParamsMessage>
                  *Não há parâmetros de configuração disponíveis para a seguradora
                  selecionada.
                </NoParamsMessage>
              )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => setOpenModalConfig(false)}>
              <Icon name="remove" />
              Sair
            </Button>
            <Button color="green" onClick={() => setOpenModalConfig(false)}>
              <Icon name="checkmark" /> Salvar
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  };

  return (
    <>
      <SectionLabel text="SEGURO INCÊNDIO" />
      <FireInsurersRow>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '1rem',
            flexWrap: 'wrap'
          }}
        >
          {availableInsurers.map((insurerOption: any) => (
            <div
              key={insurerOption.id}
              style={{
                display: 'flex',
                flex: '0 0 calc((100% - 32px) / 3)',
                gap: '1rem',
                alignItems: 'center',
                marginBottom: '1rem',
                cursor: 'pointer'
              }}
              onClick={() => setInsurerSelected(insurerOption)}
            >
              <InsurerCard
                key={insurerOption.id}
                isSelected={insurerSelected?.id === insurerOption.id}
                onClick={() => setInsurerSelected(insurerOption)}
              >
                <div
                  style={{
                    width: '120px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Image src={insurerOption.image} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                  >
                    <span>{insurerOption.name}</span>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      <Toggle
                        name={insurerOption.id}
                        id={`updateEstate-fireInsurance-${insurerOption.code}`}
                        label={
                          values.fireInsurers.find((i: any) => i.id === insurerOption.id)
                            ? 'ATIVA'
                            : 'INATIVA'
                        }
                        checked={
                          !!values.fireInsurers.find(
                            (i: any) => i.id === insurerOption.id
                          )
                        }
                        onChange={handleToggle}
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      border: '1px solid rgba(34, 36, 38, 0.15)',
                      borderRadius: '5px',
                      padding: '10px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <IconButton
                      name="cog"
                      style={{ fontSize: 24, margin: '2px 0 0 0', cursor: 'pointer' }}
                      onClick={() => setOpenModalConfig(true)}
                    />
                  </div>
                </div>
              </InsurerCard>
            </div>
          ))}
        </div>

        {insurerSelected && FireInsuranceConfigModal(props)}
      </FireInsurersRow>
    </>
  );
};
